import React, { Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { App as AntdApp, ConfigProvider, Spin, theme } from "antd";
import zhCN from "antd/locale/zh_CN";
import AdminLayout from "./layouts/AdminLayout";
import BuildVersionGuard from "./layouts/BuildVersionGuard";
import { emberHearthTheme } from "./theme/antd-theme";

/**
 * /employee-onboarding 老路由 redirect (Ricky 2026-05-29 UX 简化).
 *
 * 兼容书签 / 已分发邀请 deep-link `/employee-onboarding?inviteId=N`,
 * 透传 inviteId 到合并后的 /employees?tab=invites&inviteId=N.
 *
 * 用 useLocation + Navigate(to with search) 替代 Navigate(to) 字符串模式,
 * 因 Navigate(to='/employees?tab=invites') 会丢原 URL 的 search params.
 */
const EmployeeOnboardingRedirect: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("tab", "invites");
  return (
    <Navigate
      to={{ pathname: "/employees", search: `?${params.toString()}` }}
      replace
    />
  );
};

const HomePage = React.lazy(() => import("./pages/HomePage"));
const AttendancePage = React.lazy(() => import("./pages/AttendancePage"));
const AttendanceAmendmentPage = React.lazy(
  () => import("./pages/attendance-amendment/AttendanceAmendmentPage"),
);
const ApprovalsPage = React.lazy(() => import("./pages/ApprovalsPage"));
const EmployeeListPage = React.lazy(
  () => import("./pages/employees/EmployeeListPage"),
);
const SchedulingPage = React.lazy(
  () => import("./pages/scheduling/SchedulingPage"),
);
const InventoryPage = React.lazy(
  () => import("./pages/inventory/InventoryPage"),
);
const UnifiedInspectionPage = React.lazy(
  () => import("./pages/inspections/UnifiedInspectionPage"),
);
const SalaryStandardsPage = React.lazy(
  () => import("./pages/salary-standards/SalaryStandardsPage"),
);
const StoreProfilePage = React.lazy(
  () => import("./pages/store-profile/StoreProfilePage"),
);
const PerformancePage = React.lazy(
  () => import("./pages/performance/PerformancePage"),
);
const DishCategoryPage = React.lazy(
  () => import("./pages/dish-category/DishCategoryPage"),
);
const AnticheatCluePage = React.lazy(
  () => import("./pages/anticheat-clues/AnticheatCluePage"),
);
const CustomerVoicePage = React.lazy(
  () => import("./pages/customer-voice/CustomerVoicePage"),
);
const WeeklyReportPage = React.lazy(
  () => import("./pages/weekly-report/WeeklyReportPage"),
);
const AiWeeklyPage = React.lazy(() => import("./pages/ai-weekly/AiWeeklyPage"));
const FinanceStatementsPage = React.lazy(
  () => import("./pages/finance-statements/FinanceStatementsPage"),
);
const ProcurementPage = React.lazy(
  () => import("./pages/procurement/ProcurementPage"),
);
// /employee-onboarding 路由已合并到 /employees?tab=invites (Ricky 2026-05-29 UX 简化).
// 老链接 redirect 见 router 表 (兼容书签 / 已分发链接). 详见 EmployeeListPage Segmented tab.

/**
 * 仅 dev / test 构建可达的崩溃路由, 用于验证 ErrorBoundary 把渲染异常降级为卡片而非整页白屏。
 * 生产构建中 `import.meta.env.DEV === false`, Vite 会把该路由静态裁剪掉。
 */
const CrashTestRoute: React.FC = () => {
  throw new Error("ErrorBoundary 验证: 故意抛出的渲染异常");
};

function routerBasename(): string | undefined {
  if (
    window.location.pathname === "/admin" ||
    window.location.pathname.startsWith("/admin/")
  ) {
    return "/admin";
  }
  return undefined;
}

function withPageLoading(element: React.ReactNode): React.ReactElement {
  return (
    <Suspense
      fallback={
        <div className="route-spinner">
          <Spin />
        </div>
      }
    >
      {element}
    </Suspense>
  );
}

/**
 * D4 (DISC-039 关闭): 切到 `createBrowserRouter` + `<RouterProvider>` (data router),
 * 仅为打通 `useBlocker` 拦应用内任意导航 (含浏览器后退/前进键), 数据流不变。
 * 不引入 loader/action; route 表对象化与 basename 等价保留。
 */
const router = createBrowserRouter(
  [
    {
      element: <AdminLayout />,
      children: [
        { index: true, element: withPageLoading(<HomePage />) },
        { path: "/employees", element: withPageLoading(<EmployeeListPage />) },
        { path: "/scheduling", element: withPageLoading(<SchedulingPage />) },
        { path: "/attendance", element: withPageLoading(<AttendancePage />) },
        {
          path: "/attendance-amendment",
          element: withPageLoading(<AttendanceAmendmentPage />),
        },
        { path: "/approvals", element: withPageLoading(<ApprovalsPage />) },
        { path: "/inventory", element: withPageLoading(<InventoryPage />) },
        { path: "/procurement", element: withPageLoading(<ProcurementPage />) },
        { path: "/ai-weekly", element: withPageLoading(<AiWeeklyPage />) },
        {
          path: "/weekly-report",
          element: withPageLoading(<WeeklyReportPage />),
        },
        {
          path: "/finance-statements",
          element: withPageLoading(<FinanceStatementsPage />),
        },
        {
          path: "/customer-voice",
          element: withPageLoading(<CustomerVoicePage />),
        },
        {
          path: "/anticheat-clues",
          element: withPageLoading(<AnticheatCluePage />),
        },
        {
          path: "/inspections",
          element: withPageLoading(<UnifiedInspectionPage />),
        },
        {
          path: "/photo-compliance",
          element: <Navigate to="/inspections?tab=photo" replace />,
        },
        {
          path: "/salary-standards",
          element: withPageLoading(<SalaryStandardsPage />),
        },
        {
          path: "/store-profile",
          element: withPageLoading(<StoreProfilePage />),
        },
        { path: "/performance", element: withPageLoading(<PerformancePage />) },
        {
          path: "/dish-category",
          element: withPageLoading(<DishCategoryPage />),
        },
        // Ricky 2026-05-29 UX 简化: /employee-onboarding 合并到 /employees?tab=invites,
        // 老链接 redirect 兼容书签 / 已分发邀请详情 deep-link (透传 ?inviteId=N).
        {
          path: "/employee-onboarding",
          element: <EmployeeOnboardingRedirect />,
        },
        ...(import.meta.env.DEV
          ? [{ path: "/__crash-test", element: <CrashTestRoute /> }]
          : []),
      ],
    },
    { path: "*", element: <Navigate to="/" replace /> },
  ],
  {
    basename: routerBasename(),
  },
);

const App: React.FC = () => {
  return (
    <ConfigProvider
      locale={zhCN}
      button={{ autoInsertSpace: false }}
      // IN-001: use AntD 6's public semantic styles so tall dialogs keep their
      // header/footer visible while only the body scrolls.
      modal={{
        classNames: {
          container: "ember-modal-container",
          header: "ember-modal-header",
          body: "ember-modal-scroll-body",
          footer: "ember-modal-footer",
        },
        styles: {
          container: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 160px)",
          },
          header: { flex: "none" },
          footer: { flex: "none" },
          body: { minHeight: 0, overflow: "auto" },
        },
      }}
      theme={{
        // 视觉语言 2.0「炉火 Ember Hearth」(SPEC §⑦; 替换 1.0 品牌蓝内联 theme).
        // algorithm 由调用侧保留, 主题 token/components 全收 emberHearthTheme.
        algorithm: theme.defaultAlgorithm,
        ...emberHearthTheme,
      }}
    >
      <AntdApp>
        <BuildVersionGuard />
        <RouterProvider router={router} />
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
